// `Sensitive<T>` wraps secret material (passwords, private keys, etc.) so it
// cannot flow where a `T` is expected without an explicit `.unwrap()`, and
// so any accidental serialization (JSON.stringify, util.inspect, String,
// template strings) yields a distinctive redaction sentinel — not the bytes.
//
// Round-trip defense: the factory refuses to wrap the sentinel, and
// `zodSensitive` refuses to parse it. A serialized Sensitive that flows back
// to a wrap site (Prisma round-trip, cache replay, etc.) fails loudly.

import * as z from 'zod';

// Distinctive enough that DB inspections / log greps / schema parsers flag it
// as "not real data." Don't change without considering stored audit-log
// entries and existing refines that depend on the exact value.
export const SENSITIVE_REDACTED_SENTINEL = '<sensitive value never unwrapped — see Sensitive.ts>';

declare const sensitiveTag: unique symbol;

export type Sensitive<T> = {
  // Phantom brand — prevents `Sensitive<T>` from satisfying `T`.
  readonly [sensitiveTag]: T;
  unwrap(): T;
  toJSON(): typeof SENSITIVE_REDACTED_SENTINEL;
  toString(): typeof SENSITIVE_REDACTED_SENTINEL;
};

export const Sensitive = <T>(value: T): Sensitive<T> => {
  // `String(value)` catches the sentinel whether it arrives as a string, a
  // Buffer (default toString is utf8), or another Sensitive (overridden
  // toString returns the sentinel — blocks accidental double-wrap).
  if (String(value) === SENSITIVE_REDACTED_SENTINEL) {
    throw new Error('Refusing to wrap the Sensitive redaction sentinel — likely a round-trip bug.');
  }
  return {
    unwrap: () => value,
    toJSON: () => SENSITIVE_REDACTED_SENTINEL,
    toString: () => SENSITIVE_REDACTED_SENTINEL,
    // Node util.inspect lookup — covers console.log, pino, etc.
    [Symbol.for('nodejs.util.inspect.custom')]: () => SENSITIVE_REDACTED_SENTINEL,
  } as unknown as Sensitive<T>;
};

export const isSensitive = (v: unknown): v is Sensitive<unknown> =>
  typeof v === 'object' && v !== null && typeof (v as { unwrap?: unknown }).unwrap === 'function';

// Accepts an already-wrapped `Sensitive<T>` (passed through) or a raw `T`
// (validated by `inner`, then wrapped). Output is always `Sensitive<T>` — a
// `.parse()` at a wire boundary gives the wrapper regardless of which shape
// arrived. The sentinel is rejected; see the round-trip defense above.
//
//   const schema = z.object({ password: zodSensitive(z.string()) });
//   schema.parse({ password: 'hunter2' }).password.unwrap(); // 'hunter2'
export const zodSensitive = <T extends z.ZodType>(inner: T) => {
  type Inner = z.infer<T>;
  return z.union([
    z.custom<Sensitive<Inner>>(isSensitive),
    inner
      .refine((v) => String(v) !== SENSITIVE_REDACTED_SENTINEL, {
        message: 'Refusing to wrap the redaction sentinel — likely a round-tripped Sensitive value.',
      })
      .transform((v): Sensitive<Inner> => Sensitive(v as Inner)),
  ]);
};
